import { useContext, useEffect, useMemo, useRef } from 'react'
import { matchPath } from 'react-router-dom'
import ModalContext from 'contexts/ModalContext'
import MasterModalContext from 'contexts/MasterModalContext'
import useQueryParams from './useQueryParams'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { replaceSearchString } from 'actions/NavigationActions'
import SignupContinueModal from 'components/Account/AccountAccess/SignupContinueModal'

const LOGIN_EXCLUDED_ROUTES = [
  '/:regionCode/checkout',
  '/:regionCode/cart',
  '/:regionCode/account/communication-preferences/unsubscribe',
]

/**
 * Auto opens the sign up modal when 'sign_up' query param is present
 * Modal will not trigger on blacklisted routes.
 */
export default function useShowLoginFromUrl() {
  const showModal = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)
  const query = useQueryParams()
  const isForceLogin = query.has('sign_up')
  const dispatch = useAppDispatch()
  const capturedQuery = useRef<URLSearchParams | undefined>(undefined)
  const pendingSignup = useRef<{ token?: string } | undefined>(undefined)

  const loggedIn = useAppSelector(selectLoggedIn)
  const currentPath = useAppSelector(state => state.router.location.pathname)

  const isBlacklistedRoute = useMemo(() => {
    return LOGIN_EXCLUDED_ROUTES.some(path => matchPath({ path, end: false }, currentPath))
  }, [currentPath])

  useEffect(() => {
    const cannotOpen = loggedIn || modalState.open || isBlacklistedRoute
    if (!isForceLogin) capturedQuery.current = undefined
    if (loggedIn) pendingSignup.current = undefined
    if (isForceLogin && capturedQuery.current !== query) {
      // Capture each navigation once, including when React replays this effect.
      capturedQuery.current = query
      pendingSignup.current = loggedIn ? undefined : { token: query.get('continue') ?? undefined }
      dispatch(replaceSearchString({
        sign_up: undefined,
        continue: undefined,
      }))
    }
    if (cannotOpen || !pendingSignup.current) return
    const { token } = pendingSignup.current
    // Only the modal needs the token after handoff. A later link can open again.
    pendingSignup.current = undefined
    showModal(token ? <SignupContinueModal token={token} /> : <AccountAccessModal initialMode="login" />)
  }, [loggedIn, modalState.open, isBlacklistedRoute, showModal, dispatch, isForceLogin, query])
}
